import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, QueuedSend } from '../../store';
import ComposeNew from '../ComposeNew';
import Composer from '../Composer';

// Undo send (Calem, report ef1507ed). While a sent email waits out the user's Undo send seconds
// (store.queueSend), a card at the bottom left counts down — "Sending in N s… Undo" — and leaving or
// reloading the page asks first (the browser's own "Leave site?"). A page closed anyway posts nothing:
// the email stays in Drafts. Undo opens its composer again over whatever page this is, as it was when
// Send was pressed — the same draft, the attachments, and the reply or forward it belongs to.
// Mounted once, in App, so it outlives the composer and the page that sent it.
export default function UndoSend() {
  const sendQueue = useStore(s => s.sendQueue);
  const undoneSends = useStore(s => s.undoneSends);
  const waiting = sendQueue.length > 0;
  useEffect(() => {
    if (!waiting) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [waiting]);
  const close = (id: string) => useStore.getState().closeUndone(id);
  return (<>
    {undoneSends.map(u => u.reopen.kind === 'reply'
      ? <Composer key={u.id} email={u.reopen.email} mode={u.reopen.mode} restore={u.restore} onClose={() => close(u.id)} />
      : <ComposeNew key={u.id} draft={u.reopen.draft} restore={u.restore} onClose={() => close(u.id)} />)}
    {/* Above the composers (2000), so Undo stays in reach while another email is being written. Not a
        live region: a screen reader would read the countdown out every second. */}
    {waiting && (
      <div style={{ position: 'fixed', left: 24, bottom: 24, zIndex: 2100, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sendQueue.map(q => <Countdown key={q.id} q={q} />)}
      </div>
    )}
  </>);
}

// One card, ticking on its own so the composers above don't re-render with it.
function Countdown({ q }: { q: QueuedSend }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(tick);
  }, []);
  return (
    <div title={q.subject} style={{ background: '#085041', color: '#9FE1CB', padding: '7px 8px 7px 16px', borderRadius: 'var(--radius-lg)', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10, animation: 'slideUp 0.25s ease', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      <i className="ti ti-send" style={{ fontSize: 14 }} aria-hidden />
      <span>{t('eml_undo_sending_in', { n: Math.max(1, Math.ceil((q.sendAt - now) / 1000)) })}</span>
      <button onClick={() => useStore.getState().undoSend(q.id)}
        style={{ border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>
        {t('eml_undo_btn')}
      </button>
    </div>
  );
}
